import type { Action, OrgRole, Resource } from '@carbonwebmail/domain'
import { may } from '@carbonwebmail/domain'
import { createContext, type ReactNode, useContext } from 'react'

/**
 * Your role, where a screen can reach it without being handed it.
 *
 * **The server already refuses what you may not do; this is about not offering
 * it.** `createRequirePermission` is the control, and nothing here is — a role
 * held in a browser is a hint, and gating a button on it stops a mistake, not
 * an attacker. What it fixes is a product that invites people to fail: before
 * this, a viewer was shown "New campaign", a member was shown an invitation
 * form whose role list was empty, and both got a 403 for taking the offer.
 *
 * **A context rather than a prop, because the alternative was the reason it
 * never got done.** `yourRole` reached three components by prop and would have
 * had to reach about a dozen more — through boards, toolbars and dialogs that
 * have no other interest in who you are — and a prop threaded through five
 * layers to decide one button is a prop that gets dropped at layer three.
 * `SettingsBoard` had already written the intent down ("the screens inside
 * decide what is editable") and no screen inside did.
 *
 * The provider is `AppShell`, which is the only thing wrapping every signed-in
 * screen and already takes the role for its own sidebar filtering. Screens
 * outside it — sign-in, the invitation page, the legal pages — have no role
 * because nobody is signed in on them, and nothing on them is gated.
 */
const YourRoleContext = createContext<OrgRole | undefined>(undefined)

/**
 * `yourRole` rather than `role`, which is not a style choice.
 *
 * `role` is an ARIA attribute, and a prop of that name on a component is read
 * as one by the accessibility lint — a rule worth keeping, so the prop takes
 * the name the rest of this codebase already uses for the same value.
 */
export function YourRoleProvider({
  yourRole,
  children,
}: {
  readonly yourRole: OrgRole
  readonly children: ReactNode
}) {
  return <YourRoleContext.Provider value={yourRole}>{children}</YourRoleContext.Provider>
}

/**
 * Throws when there is no provider above, rather than assuming a role.
 *
 * The same reasoning as the permission middleware, which throws when it is
 * mounted without a session guard: a missing provider is a mounting mistake,
 * and both ways of papering over it are worse than a crash. Defaulting to
 * `viewer` would hide working buttons from an owner and look like a
 * permissions bug; defaulting to `owner` would show every button to everyone,
 * which is the state this file exists to end.
 */
export function useYourRole(): OrgRole {
  const role = useContext(YourRoleContext)
  if (role === undefined) {
    throw new Error('useYourRole was called outside YourRoleProvider')
  }
  return role
}

/**
 * Whether to offer the control at all. The same question the server asks.
 *
 * Asked through the domain's own table rather than by comparing role names
 * here, so a screen and the route behind it can never disagree about what a
 * role may do — the drift that a second copy of the rules would make
 * inevitable.
 */
export function useMay(resource: Resource, action: Action): boolean {
  return may(useYourRole(), resource, action)
}
